import { For, Show, createMemo, createSignal } from "solid-js";
import { useApi } from "../../api/provider.tsx";
import { Async } from "../../components/Async.tsx";
import { Form, SelectField, Toggle } from "../../components/Form.tsx";
import { Panel } from "../../components/Panel.tsx";
import { useI18n } from "../../i18n/provider.tsx";
import { createLoader, loadedValue } from "../../lib/async.ts";
import { assignedQuestionIds } from "../../lib/tree.ts";
import { formatScore } from "../../lib/format.ts";
import {
  groupAverage,
  hasAnswers,
  overviewGroups,
  unassignedGroup,
  type OverviewGroup,
  type OverviewQuestion,
} from "./overview.ts";
import type { Product, QuestionCoverage } from "../../api/domain.ts";
import styles from "./overview.module.scss";
import admin from "./admin.module.scss";

/** The same three-state vocabulary the catalog tree uses, so the two agree. */
const coverageStyle: Record<QuestionCoverage, string> = {
  counted: styles.counted ?? "",
  outside: styles.outside ?? "",
  unanswered: styles.unanswered ?? "",
};

/**
 * Every answer on file, question by question, product by product.
 *
 * This is the read that does not go through a capability. A report derives its
 * question set from capabilities alone, so an answer to a question no
 * capability claims contributes nothing and looks identical to no answer at
 * all. Here questions hang off the topic that lists them instead, which is why the
 * page can promise that nothing recorded is missing from it.
 */
export const OverviewPage = () => {
  const api = useApi();
  const i18n = useI18n();

  /**
   * Which view the page is read through. It narrows the products and the
   * topics, and it decides what the coverage badges say. Empty means a view
   * that narrows nothing, which is the default because the page's whole point
   * is that nothing is hidden.
   */
  const [through, setThrough] = createSignal("");
  const [onlyAnswered, setOnlyAnswered] = createSignal(false);

  const views = createLoader(() => api.list("views", { lang: i18n.locale() }));
  const products = createLoader(() => api.list("products", { lang: i18n.locale() }));
  const questions = createLoader(() => api.list("questions", { lang: i18n.locale() }));
  const answers = createLoader(() => api.list("answers"));
  const tree = createLoader(() =>
    api.tree({ lang: i18n.locale(), ...(through() === "" ? {} : { view: through() }) }),
  );

  const chosen = createMemo(() =>
    (loadedValue(views.value()) ?? []).find((view) => view.id === through()),
  );

  /**
   * The columns, chosen the way the tree endpoint chooses which products to
   * answer for: the view's products when it names any, every active product
   * otherwise. Matching that rule is what keeps a column from standing empty
   * because the server never filled it.
   */
  const columns = createMemo<Product[]>(() => {
    const all = loadedValue(products.value()) ?? [];
    const wanted = chosen()?.products ?? [];
    return wanted.length === 0
      ? all.filter((product) => product.active)
      : all.filter((product) => wanted.includes(product.id));
  });

  const groups = createMemo<OverviewGroup[]>(() => {
    const topics = loadedValue(tree.value()) ?? [];
    const fromTree = overviewGroups(topics);
    // Only worth asking about orphans once the flat catalog has arrived; until
    // then the tree's own questions would all look unassigned.
    const flat = loadedValue(questions.value());
    const orphans =
      flat === undefined
        ? undefined
        : unassignedGroup(flat, assignedQuestionIds(topics), loadedValue(answers.value()) ?? []);
    const all = orphans === undefined ? fromTree : [...fromTree, orphans];
    return onlyAnswered() ? all.filter(hasAnswers) : all;
  });

  const heading = (group: OverviewGroup): string =>
    group.topic === undefined ? i18n.t().overview.unassigned : group.path.join(" / ");

  const Cell = (cell: { question: OverviewQuestion; product: string }) => {
    const answer = () => cell.question.answers[cell.product];
    const recorded = () => cell.question.recorded[cell.product] ?? 0;

    return (
      <Show
        when={answer()}
        fallback={
          <Show when={recorded() > 0} fallback={<span class={styles.absent}>—</span>}>
            <span class={styles.recorded}>
              {recorded()} {i18n.t().overview.recorded}
            </span>
          </Show>
        }
      >
        {(found) => (
          <span class={styles.answer} title={`${found().author} · ${found().source}`}>
            <Show
              when={found().value !== null && found().value !== undefined}
              fallback={<span class={styles.text}>{found().text ?? "—"}</span>}
            >
              {formatScore(found().value as number, i18n.locale())}
            </Show>
          </span>
        )}
      </Show>
    );
  };

  return (
    <>
      <div class={admin.heading}>
        <h1>{i18n.t().overview.title}</h1>
      </div>

      <Panel>
        <Form>
          <SelectField
            label={i18n.t().coverage.view}
            hint={i18n.t().coverage.viewHint}
            placeholder={i18n.t().coverage.noView}
            optional
            choices={(loadedValue(views.value()) ?? []).map((view) => ({
              id: view.id,
              label: view.label,
            }))}
            value={through()}
            onPick={(view) => {
              setThrough(view);
              tree.reload();
            }}
          />
          <Toggle
            label={i18n.t().overview.onlyAnswered}
            checked={onlyAnswered()}
            onToggle={setOnlyAnswered}
          />
          <p class={admin.note}>{i18n.t().overview.onlyAnsweredHint}</p>
        </Form>
        <p class={admin.note}>{i18n.t().overview.caveat}</p>
      </Panel>

      <Async value={tree.value()} onRetry={tree.reload}>
        {() => (
          <Show
            when={columns().length > 0}
            fallback={<p class={admin.note}>{i18n.t().overview.noProducts}</p>}
          >
            <Show
              when={groups().length > 0}
              fallback={<p class={admin.note}>{i18n.t().overview.empty}</p>}
            >
              <div class={styles.scroll}>
                <table class={styles.matrix}>
                  <thead>
                    <tr>
                      <th class={styles.head} scope="col">
                        {i18n.t().common.question}
                      </th>
                      <For each={columns()}>
                        {(product) => (
                          <th class={`${styles.head} ${styles.product}`} scope="col">
                            {product.name}
                          </th>
                        )}
                      </For>
                    </tr>
                  </thead>
                  <For each={groups()}>
                    {(group) => (
                      <tbody class={styles.group}>
                        <tr>
                          <th class={styles.groupHead} colSpan={columns().length + 1} scope="colgroup">
                            {heading(group)}
                            <Show when={group.topic === undefined}>
                              <span class={styles.groupHint}>
                                {i18n.t().overview.unassignedHint}
                              </span>
                            </Show>
                          </th>
                        </tr>
                        <For each={group.questions}>
                          {(question) => (
                            <tr class={styles.row}>
                              <th class={styles.questionCell} scope="row">
                                <span class={styles.questionTitle}>{question.title}</span>
                                <Show when={question.coverage}>
                                  {(coverage) => (
                                    <span
                                      class={`${styles.badge} ${coverageStyle[coverage()]}`}
                                      title={i18n.t().coverage.hint[coverage()]}
                                    >
                                      {i18n.t().coverage.label[coverage()]}
                                    </span>
                                  )}
                                </Show>
                                <Show when={question.informational}>
                                  <span class={styles.badge}>
                                    {i18n.t().catalog.informational}
                                  </span>
                                </Show>
                              </th>
                              <For each={columns()}>
                                {(product) => (
                                  <td class={styles.cell}>
                                    <Cell question={question} product={product.id} />
                                  </td>
                                )}
                              </For>
                            </tr>
                          )}
                        </For>
                        {/* Not a score: the plain mean of what this product answered here. */}
                        <Show when={group.topic !== undefined}>
                          <tr class={styles.averageRow}>
                            <th class={styles.questionCell} scope="row">
                              {i18n.t().overview.topicAverage}
                            </th>
                            <For each={columns()}>
                              {(product) => {
                                const average = () => groupAverage(group, product.id);
                                return (
                                  <td class={styles.cell}>
                                    <Show
                                      when={average().mean !== undefined}
                                      fallback={<span class={styles.absent}>—</span>}
                                    >
                                      <span class={styles.mean}>
                                        {formatScore(average().mean as number, i18n.locale())}
                                      </span>
                                    </Show>
                                    <span class={styles.thinness}>
                                      {average().answered} {i18n.t().overview.answeredOf}{" "}
                                      {average().total}
                                    </span>
                                  </td>
                                );
                              }}
                            </For>
                          </tr>
                        </Show>
                      </tbody>
                    )}
                  </For>
                </table>
              </div>
            </Show>
          </Show>
        )}
      </Async>
    </>
  );
};
